import * as React from "react";
import * as ReactDOM from "react-dom";

import { emptyRef } from "../../TypingUtils";

import { DomElementHack } from "./DomElementHack";
import { toHtmlElementOrNull } from "./DomHelpers";

type DomElementsRegistryContext = Map<string, React.RefObject<DomElementHack>>;

const DomElementsRegistryContextObject = React.createContext<DomElementsRegistryContext>(new Map());

export function RegisterDomElement(props: { children: React.ReactNode; id: string | undefined }): JSX.Element {
    const domElementRef = React.useRef<DomElementHack>(emptyRef);
    const context = React.useContext(DomElementsRegistryContextObject);
    React.useEffect(() => {
        if (props.id != undefined) {
            context.set(props.id, domElementRef);
        }
    }, [props.id]);
    return <DomElementHack ref={domElementRef}>{props.children}</DomElementHack>;
}

export interface ScrollToContextHandle {
    getDomElementById: (id: undefined | string) => HTMLElement | undefined;
}

export const DomElementsRegistry = React.forwardRef(
    (props: { children: React.ReactNode }, ref: React.Ref<ScrollToContextHandle>) => {
        const unitIdToComponentMap = React.useRef<DomElementsRegistryContext>(new Map());

        const getDomElementById = React.useCallback((id: undefined | string): HTMLElement | undefined => {
            if (id == undefined) {
                return undefined;
            }
            const elementRef = unitIdToComponentMap.current.get(id);
            return toHtmlElementOrNull(ReactDOM.findDOMNode(elementRef?.current)) ?? undefined;
        }, []);

        React.useImperativeHandle(
            ref,
            () => ({
                getDomElementById: getDomElementById,
            }),
            []
        );
        return (
            <DomElementsRegistryContextObject.Provider value={unitIdToComponentMap.current}>
                {props.children}
            </DomElementsRegistryContextObject.Provider>
        );
    }
);
DomElementsRegistry.displayName = "DomElementsRegistryContext";
